/**
 * CartCheckoutPage duck — manages state for Shippo-first cart checkout.
 *
 * Flow (Shippo item):
 * 1. User chooses payment method (card / express)
 * 2. Address modal → fetchAllRatesForItem → rate selection modal
 * 3. Payment form → processCartCheckout (single item) → removeFromCart
 */

import { shippoGetRates, initiatePrivileged, confirmPayment } from '../../util/api';
import { storableError } from '../../util/errors';
import { types as sdkTypes } from '../../util/sdkLoader';
import { removeFromCart } from '../../ducks/cart.duck';

const { UUID } = sdkTypes;

// ================ Action types ================ //

export const FETCH_RATES_REQUEST = 'app/CartCheckoutPage/FETCH_RATES_REQUEST';
export const FETCH_RATES_SUCCESS = 'app/CartCheckoutPage/FETCH_RATES_SUCCESS';
export const FETCH_RATES_ERROR = 'app/CartCheckoutPage/FETCH_RATES_ERROR';

export const FETCH_ALL_RATES_REQUEST = 'app/CartCheckoutPage/FETCH_ALL_RATES_REQUEST';
export const FETCH_ALL_RATES_SUCCESS = 'app/CartCheckoutPage/FETCH_ALL_RATES_SUCCESS';
export const FETCH_ALL_RATES_ERROR = 'app/CartCheckoutPage/FETCH_ALL_RATES_ERROR';

export const SELECT_RATE = 'app/CartCheckoutPage/SELECT_RATE';

export const PROCESS_ITEM_START = 'app/CartCheckoutPage/PROCESS_ITEM_START';
export const PROCESS_ITEM_SUCCESS = 'app/CartCheckoutPage/PROCESS_ITEM_SUCCESS';

export const CHECKOUT_ERROR = 'app/CartCheckoutPage/CHECKOUT_ERROR';
export const CHECKOUT_COMPLETE = 'app/CartCheckoutPage/CHECKOUT_COMPLETE';
export const CHECKOUT_RESET = 'app/CartCheckoutPage/CHECKOUT_RESET';

// ================ Reducer ================ //

const initialState = {
  // Selected rate per item (cheapest by default, updated on SELECT_RATE)
  // { [listingId]: { rate: { amount, currency, provider, estimatedDays }, loading: bool, error: null|string } }
  rates: {},
  ratesLoading: false,

  // All available Shippo rates per item (for rate selection modal)
  // { [listingId]: { rates: [], loading: bool, error: null } }
  availableRates: {},

  // Sequential processing state
  processingIndex: -1, // -1 = not started
  completedOrders: [], // [{ listingId, transactionId, title, shippingAddress, shippingRate, imageUrl }]
  checkoutError: null,
  checkoutInProgress: false,
};

export default function cartCheckoutReducer(state = initialState, action = {}) {
  const { type, payload } = action;

  switch (type) {
    // ── Legacy batch rate fetching (kept for backward compat) ──────────────
    case FETCH_RATES_REQUEST:
      return {
        ...state,
        ratesLoading: true,
        rates: payload.listingIds.reduce((acc, id) => {
          acc[id] = { rate: null, loading: true, error: null };
          return acc;
        }, { ...state.rates }),
      };

    case FETCH_RATES_SUCCESS:
      return {
        ...state,
        ratesLoading: false,
        rates: {
          ...state.rates,
          [payload.listingId]: { rate: payload.rate, loading: false, error: null },
        },
      };

    case FETCH_RATES_ERROR:
      return {
        ...state,
        ratesLoading: false,
        rates: {
          ...state.rates,
          [payload.listingId]: { rate: null, loading: false, error: payload.error },
        },
      };

    // ── All rates (for rate selection modal) ───────────────────────────────
    case FETCH_ALL_RATES_REQUEST:
      return {
        ...state,
        availableRates: {
          ...state.availableRates,
          [payload.listingId]: { rates: [], loading: true, error: null },
        },
      };

    case FETCH_ALL_RATES_SUCCESS:
      return {
        ...state,
        availableRates: {
          ...state.availableRates,
          [payload.listingId]: { rates: payload.rates, loading: false, error: null },
        },
        // Also set the cheapest as the default selected rate
        rates: {
          ...state.rates,
          [payload.listingId]: { rate: payload.rates[0] || null, loading: false, error: null },
        },
      };

    case FETCH_ALL_RATES_ERROR:
      return {
        ...state,
        availableRates: {
          ...state.availableRates,
          [payload.listingId]: { rates: [], loading: false, error: payload.error },
        },
      };

    // ── User selects a rate ────────────────────────────────────────────────
    case SELECT_RATE:
      return {
        ...state,
        rates: {
          ...state.rates,
          [payload.listingId]: { rate: payload.rate, loading: false, error: null },
        },
      };

    // ── Checkout flow ──────────────────────────────────────────────────────
    case PROCESS_ITEM_START:
      return {
        ...state,
        checkoutInProgress: true,
        checkoutError: null,
        processingIndex: payload.index,
      };

    case PROCESS_ITEM_SUCCESS: {
      const newOrder = {
        listingId: payload.listingId,
        transactionId: payload.transactionId,
        title: payload.title,
        deliveryMethod: payload.deliveryMethod,
        shippingAddress: payload.shippingAddress,
        shippingRate: payload.shippingRate,
        imageUrl: payload.imageUrl,
        sellerName: payload.sellerName,
        fulfiller: payload.fulfiller,
        quantity: payload.quantity,
        price: payload.price,
        currency: payload.currency,
      };
      // Persist so the confirmation page survives a refresh
      try {
        localStorage.setItem('bowtackle_last_order', JSON.stringify(newOrder));
      } catch (e) { /* ignore quota errors */ }
      return {
        ...state,
        completedOrders: [
          ...state.completedOrders,
          {
            listingId: payload.listingId,
            transactionId: payload.transactionId,
            title: payload.title,
            price: payload.price,
            deliveryMethod: payload.deliveryMethod,
            shippingAddress: payload.shippingAddress,
            shippingRate: payload.shippingRate,
            imageUrl: payload.imageUrl,
            sellerName: payload.sellerName,
            fulfiller: payload.fulfiller,
          },
        ],
      };
    }

    case CHECKOUT_ERROR:
      return {
        ...state,
        checkoutInProgress: false,
        checkoutError: payload,
      };

    case CHECKOUT_COMPLETE:
      return {
        ...state,
        checkoutInProgress: false,
        processingIndex: -1,
      };

    case CHECKOUT_RESET:
      try { localStorage.removeItem('bowtackle_last_order'); } catch (e) { /* ignore */ }
      return initialState;

    default:
      return state;
  }
}

// ================ Action creators ================ //

export const fetchRatesRequest = listingIds => ({ type: FETCH_RATES_REQUEST, payload: { listingIds } });
export const fetchRatesSuccess = (listingId, rate) => ({ type: FETCH_RATES_SUCCESS, payload: { listingId, rate } });
export const fetchRatesError = (listingId, error) => ({ type: FETCH_RATES_ERROR, payload: { listingId, error } });

export const fetchAllRatesRequest = listingId => ({ type: FETCH_ALL_RATES_REQUEST, payload: { listingId } });
export const fetchAllRatesSuccess = (listingId, rates) => ({ type: FETCH_ALL_RATES_SUCCESS, payload: { listingId, rates } });
export const fetchAllRatesError = (listingId, error) => ({ type: FETCH_ALL_RATES_ERROR, payload: { listingId, error } });

export const selectRate = (listingId, rate) => ({ type: SELECT_RATE, payload: { listingId, rate } });

export const processItemStart = index => ({ type: PROCESS_ITEM_START, payload: { index } });
export const processItemSuccess = data => ({ type: PROCESS_ITEM_SUCCESS, payload: data });
export const checkoutError = error => ({ type: CHECKOUT_ERROR, payload: error });
export const checkoutComplete = () => ({ type: CHECKOUT_COMPLETE });
export const checkoutReset = () => ({ type: CHECKOUT_RESET });

// ================ Thunks ================ //

/**
 * Fetch all available Shippo rates for a single item.
 * Stores them in availableRates and auto-selects cheapest into rates.
 *
 * @param {Object} item       - cart item with shippingMethod === 'buyer' and parcel
 * @param {Object} addressTo  - { name, street1, street2, city, state, zip, country }
 */
export const fetchAllRatesForItem = (item, addressTo) => async dispatch => {
  const { listingId, shippingMethod, flatAmountInCents, currency = 'USD', parcel, shippingAddress, sellerName } = item;

  dispatch(fetchAllRatesRequest(listingId));

  // Flat-rate: skip Shippo, dispatch a single synthetic rate
  if (shippingMethod === 'flat') {
    dispatch(fetchAllRatesSuccess(listingId, [{
      amount: (flatAmountInCents / 100).toFixed(2),
      currency,
      provider: 'Flat Rate',
      servicelevel: { name: 'Flat Rate' },
    }]));
    return;
  }

  // Free shipping: no rate needed
  if (!shippingMethod || shippingMethod === 'free') {
    dispatch(fetchAllRatesSuccess(listingId, []));
    return;
  }

  const hasParcel = parcel && (parcel.length > 0 || parcel.width > 0 || parcel.height > 0 || parcel.weight > 0);
  if (!hasParcel) {
    dispatch(fetchAllRatesSuccess(listingId, []));
    return;
  }

  const addressFrom = shippingAddress
    ? {
        ...shippingAddress,
        name: shippingAddress.name || sellerName || 'Seller',
        email: shippingAddress.email || 'shipping@bowtackle.com',
      }
    : null;

  try {
    const result = await shippoGetRates({ addressFrom, addressTo, parcels: [parcel], listingId });
    const ratesArr = result?.rates || [];
    const sorted = ratesArr.sort((a, b) => parseFloat(a.amount) - parseFloat(b.amount));
    dispatch(fetchAllRatesSuccess(listingId, sorted));
  } catch (e) {
    dispatch(fetchAllRatesError(listingId, storableError(e)));
    throw e; // re-throw so caller can handle (e.g. keep address modal open)
  }
};

/**
 * Fetch cheapest Shippo rate for each cart item in parallel (legacy/batch path).
 */
export const fetchRatesForItems = (items, addressTo) => async dispatch => {
  const listingIds = items.map(i => i.listingId);
  dispatch(fetchRatesRequest(listingIds));

  await Promise.all(
    items.map(async item => {
      const { listingId, shippingMethod, flatAmountInCents, currency = 'USD', parcel, shippingAddress, sellerName } = item;

      if (shippingMethod === 'flat') {
        dispatch(fetchRatesSuccess(listingId, {
          amount: (flatAmountInCents / 100).toFixed(2),
          currency,
          provider: 'Flat Rate',
        }));
        return;
      }

      if (!shippingMethod || shippingMethod === 'free') {
        dispatch(fetchRatesSuccess(listingId, null));
        return;
      }

      const hasParcel = parcel && (parcel.length > 0 || parcel.width > 0 || parcel.height > 0 || parcel.weight > 0);
      if (!hasParcel) {
        dispatch(fetchRatesSuccess(listingId, null));
        return;
      }

      const addressFrom = shippingAddress
        ? { ...shippingAddress, name: shippingAddress.name || sellerName || 'Seller' }
        : null;

      try {
        const result = await shippoGetRates({ addressFrom, addressTo, parcels: [parcel], listingId });
        const ratesArr = result?.rates || [];
        const cheapest = ratesArr.sort((a, b) => parseFloat(a.amount) - parseFloat(b.amount))[0];
        dispatch(fetchRatesSuccess(listingId, cheapest || null));
      } catch (e) {
        dispatch(fetchRatesError(listingId, storableError(e)));
      }
    })
  );
};

/**
 * Process checkout for a single Shippo item.
 * After success: removes item from cart (other items stay).
 *
 * @param {Object} params
 * @param {Object} params.stripe          - Stripe.js instance
 * @param {Object} params.cardElement     - Stripe CardElement
 * @param {string} params.billingName
 * @param {Object} params.shippingAddress - { name, phone, street1, street2, city, state, zip, country }
 * @param {Object} params.item            - single cart item
 * @param {Object} params.rates           - { [listingId]: { rate, loading, error } }
 */
const makeUUID = () =>
  'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });

/**
 * Process checkout for all items in the ePro/WRI cart.
 *
 * Accepts EITHER:
 *   - { cardElement, billingName }   — standard card payment
 *   - { paymentMethodId }            — express checkout (Google Pay / Apple Pay)
 *
 * Handles per-item delivery:
 *   - 'pickup'   → deliveryMethod: 'pickup', no shippingDetails needed
 *   - 'shipping' → deliveryMethod: 'shipping' + shippingDetails from shippingAddress
 */
export const processCartCheckout = ({
  stripe,
  // Card payment:
  cardElement,
  billingName,
  // Express payment (GPay / APay):
  paymentMethodId,
  // Cart data:
  items,
  deliveryChoices,
  shippingAddress,
  isGuest,
}) => async (dispatch, getState) => {
  dispatch(processItemStart(0));

  try {
    // One cartOrderId shared across all items in this checkout session.
    // The export uses this to group cart items into a single file for Vince.
    const cartOrderId = makeUUID();

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      const delivery = deliveryChoices[item.listingId] || 'shipping';
      const isPickup = delivery === 'pickup';
      const shippingCents = isPickup ? 0 : (item.flatAmountInCents || 0);

      const flatRateObj =
        !isPickup && shippingCents > 0
          ? { provider: 'Flat Rate', amount: (shippingCents / 100).toFixed(2), currency: item.currency || 'USD' }
          : null;

      const shippingDetailsObj =
        !isPickup && shippingAddress
          ? {
              name: shippingAddress.name || '',
              phoneNumber: shippingAddress.phone || '',
              address: {
                city: shippingAddress.city || '',
                country: shippingAddress.country || 'US',
                line1: shippingAddress.street1 || '',
                line2: shippingAddress.street2 || '',
                postalCode: shippingAddress.zip || '',
                state: shippingAddress.state || '',
              },
            }
          : null;

      // Buyer contact info — always store when shipping address has an email,
      // regardless of auth state. When a guest logs in via the shared guest account
      // they are technically "authenticated" (isGuest=false) but their real
      // name/email/phone still need to be saved to protectedData for order exports.
      const guestContactInfo =
        shippingAddress && shippingAddress.email
          ? {
              name: shippingAddress.name || '',
              email: shippingAddress.email || '',
              phone: shippingAddress.phone || '',
              street1: shippingAddress.street1 || '',
              street2: shippingAddress.street2 || '',
              city: shippingAddress.city || '',
              state: shippingAddress.state || '',
              zip: shippingAddress.zip || '',
              country: shippingAddress.country || 'US',
            }
          : null;

      const txParams = {
        listingId: new UUID(item.listingId),
        stockReservationQuantity: item.quantity,
        deliveryMethod: isPickup ? 'pickup' : 'shipping',
        protectedData: {
          cartOrderId,
          shippingRate: flatRateObj,
          // Explicit shippingDetails so order-export reads protectedData.shippingDetails.address.*
          ...(shippingDetailsObj ? { shippingDetails: shippingDetailsObj } : {}),
          // Buyer contact info for order export; isGuestUser only set for real guests
          ...(guestContactInfo ? {
            guestContactInfo,
            guestEmail: guestContactInfo.email,
            ...(isGuest ? { isGuestUser: true } : {}),
          } : {}),
        },
        // Also pass as top-level param for Sharetribe's built-in address handling
        ...(shippingDetailsObj ? { shippingDetails: shippingDetailsObj } : {}),
      };

      // ── Step 1: request-payment (privileged) ───────────────────────────
      // For Shippo items with a pre-selected rate (from CartPage rate modal),
      // include shippingRate in orderData so server-side lineItems can use it.
      const orderDataForRequest = { deliveryMethod: isPickup ? 'pickup' : 'shipping' };
      if (!isPickup && item._shippoRate) {
        orderDataForRequest.shippingRate = item._shippoRate;
      }

      const initiateResp = await initiatePrivileged({
        isSpeculative: false,
        orderData: orderDataForRequest,
        bodyParams: {
          processAlias: 'default-purchase/release-1',
          transition: 'transition/request-payment',
          params: txParams,
        },
        queryParams: { include: ['booking', 'provider'], expand: true },
      });

      const transaction = initiateResp?.data?.data;
      if (!transaction?.id) throw new Error(`Failed to initiate order for "${item.title}"`);

      const transactionId = transaction.id;
      const clientSecret =
        transaction.attributes?.protectedData?.stripePaymentIntents?.default
          ?.stripePaymentIntentClientSecret;
      if (!clientSecret) throw new Error(`No payment intent for "${item.title}"`);

      // ── Step 2: stripe.confirmCardPayment — card OR express ────────────
      const stripeResult = paymentMethodId
        ? await stripe.confirmCardPayment(clientSecret, { payment_method: paymentMethodId })
        : await stripe.confirmCardPayment(clientSecret, {
            payment_method: { card: cardElement, billing_details: { name: billingName } },
          });

      if (stripeResult.error) throw new Error(stripeResult.error.message);

      // ── Step 3: confirm-payment ─────────────────────────────────────────
      // Route through server API so iframe auth (X-Iframe-Token / server-side
      // guest login) works. Direct sdk.transactions.transition uses cookies
      // which are blocked in iframes (especially Safari ITP).
      
      const confirmPaymentParams = { transactionId };
      // If delivery is shipping and we have a valid Shippo rate (not flat/free), send rateId to generate label
      if (!isPickup && item._shippoRate && item._shippoRate.object_id && item._shippoRate.object_id !== 'flat' && item._shippoRate.object_id !== 'free') {
        confirmPaymentParams.rateId = item._shippoRate.object_id;
      }
      
      await confirmPayment(confirmPaymentParams);

      dispatch(
        processItemSuccess({
          listingId: item.listingId,
          transactionId: transactionId.uuid,
          title: item.title,
          imageUrl: item.imageUrl,
          sellerName: item.sellerName,
          fulfiller: item.fulfiller,
          quantity: item.quantity || 1,
          price: item.price,
          currency: item.currency || 'USD',
          deliveryMethod: isPickup ? 'pickup' : 'shipping',
          shippingAddress: isPickup ? null : shippingAddress,
          shippingRate: flatRateObj,
        })
      );

      dispatch(removeFromCart(item.listingId));

      // ── Step 4: Legacy WRI payment split ───────────────────────────────────
      // Legacy reference path only. Active centralized WRI settlement is
      // server-owned: the transaction event poller invokes the centralized
      // settlement helper after payment confirmation (no browser trigger).
      if (item.source === 'wri' && item.cost > 0) {
        const isPickup_ = delivery === 'pickup';
        // WRI uses the freight table, not flatAmountInCents — compute via /api/freight
        let wriShippingCents = 0;
        if (!isPickup_) {
          try {
            const freightResp = await fetch('/api/freight', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                items: [{ price: item.price || item.cost || 0, quantity: item.quantity || 1 }],
                shipToState: shippingAddress?.state || '',
              }),
            });
            const freightData = await freightResp.json();
            wriShippingCents = freightData.freightCents || 0;
          } catch (freightErr) {
            console.warn('[CartCheckout] Could not compute WRI freight for split, defaulting to 0:', freightErr.message);
          }
        }
        try {
          const splitResp = await fetch('/api/wri/payment-split', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              transactionId: transactionId.uuid,
              costCents: item.cost,
              shippingCents: wriShippingCents,
              priceCents: item.price,
              currency: item.currency || 'USD',
            }),
          });
          if (!splitResp.ok) {
            const body = await splitResp.json().catch(() => ({}));
            throw new Error(body.error || `WRI split returned ${splitResp.status}`);
          }
        } catch (splitErr) {
          console.error('[CartCheckout] WRI payment split failed (non-blocking):', splitErr.message);
          try {
            await fetch('/api/wri/record-split-failure', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                transactionId: transactionId.uuid,
                errorMessage: splitErr.message,
                splitParams: {
                  costCents: item.cost,
                  shippingCents: wriShippingCents,
                  priceCents: item.price,
                  currency: item.currency || 'USD',
                },
              }),
            });
          } catch (recordErr) {
            console.error('[CartCheckout] Could not record WRI split failure:', recordErr.message);
          }
        }
      }
    }

    dispatch(checkoutComplete());
    return { success: true };
  } catch (err) {
    // Surface the actual error — server returns { statusText, data } (SDK error) or { error } (general)
    const isUnauthorized =
      err.status === 401 ||
      err.statusText === 'Unauthorized' ||
      err.error === 'Unauthorized' ||
      err.message === 'Unauthorized';
    const detail = err.statusText || err.error || err.message || null;
    const errMsg = isUnauthorized
      ? 'Your session expired. Please refresh the page and try again.'
      : detail ? `Checkout failed: ${detail}` : 'Checkout failed. Please try again.';
    dispatch(checkoutError({ message: errMsg }));
    return { success: false, error: errMsg };
  }
};

// ================ Selectors ================ //

export const getCartCheckoutState = state => state.CartCheckoutPage || initialState;
export const getRates = state => (state.CartCheckoutPage || initialState).rates;
export const getAvailableRates = state => (state.CartCheckoutPage || initialState).availableRates;
export const getCompletedOrders = state => (state.CartCheckoutPage || initialState).completedOrders;
export const getCheckoutInProgress = state => (state.CartCheckoutPage || initialState).checkoutInProgress;
export const getCheckoutError = state => (state.CartCheckoutPage || initialState).checkoutError;
export const getProcessingIndex = state => (state.CartCheckoutPage || initialState).processingIndex;
